(() => {
  const config = document.getElementById('HydrosInteractionLabels');
  if (!config) return;
  const labels = JSON.parse(config.textContent);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const activeMotions = new Map();
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const reveal = (element, pointer, direction = 1) => {
    activeMotions.get(element)?.cancel();
    if (!pointer || reduced.matches || !element?.animate) return;
    const motion = element.animate([
      { opacity: .5, transform: `translateX(${direction * 12}px)` },
      { opacity: 1, transform: 'translateX(0)' }
    ], { duration: 240, easing: ease });
    activeMotions.set(element, motion);
    motion.finished.then(() => { if (activeMotions.get(element) === motion) activeMotions.delete(element); }, () => {});
  };
  reduced.addEventListener('change', () => {
    if (reduced.matches) { activeMotions.forEach(motion => motion.cancel()); activeMotions.clear(); }
  });
  const button = (text, name) => {
    const node = document.createElement('button');
    node.type = 'button'; node.textContent = text;
    if (name) node.setAttribute('aria-label', name);
    return node;
  };
  const controls = () => {
    const root = document.createElement('div'); root.className = 'hydros-media-controls';
    const prev = button('←', labels.previous); const next = button('→', labels.next);
    const count = document.createElement('span'); count.className = 'hydros-media-count';
    count.setAttribute('aria-live', 'polite'); count.setAttribute('aria-atomic', 'true');
    root.append(prev, count, next);
    return { root, prev, next, count };
  };
  const enhance = (element, setup) => {
    if (states.has(element)) return;
    const events = new AbortController();
    const cleanups = [];
    states.set(element, { events, cleanups });
    setup(events.signal, cleanups);
  };
  const cards = root => root.querySelectorAll('[data-world-card]').forEach(card => enhance(card, (signal, cleanups) => {
    const media = card.querySelector('.ecom-product-card__media');
    const main = media?.querySelector('[data-world-product-image]');
    const picture = media?.querySelector('picture');
    const scene = picture?.querySelector('img');
    const source = picture?.querySelector('source');
    if (!media || !main || !scene || !source) return;
    let showingScene = false;
    const toggle = event => {
      if (event.pointerType !== 'touch') return;
      event.preventDefault();
      showingScene = !showingScene;
      card.dataset.mediaView = showingScene ? 'lifestyle' : 'studio';
      main.setAttribute('aria-hidden', String(showingScene));
      scene.setAttribute('aria-hidden', String(!showingScene));
    };
    media.addEventListener('pointerup', toggle, { signal });
    cleanups.push(() => {
      delete card.dataset.mediaView;
      media.removeEventListener('pointerup', toggle);
      main.removeAttribute('aria-hidden');
      scene.removeAttribute('aria-hidden');
    });
  }));
  const galleries = root => root.querySelectorAll('.hydros-pdp__gallery').forEach(gallery => enhance(gallery, (signal, cleanups) => {
    const thumbs = [...gallery.querySelectorAll('[data-hynox-gallery-thumb],[data-lumos-gallery-thumb]')];
    const stage = gallery.querySelector('.hydros-pdp__stage');
    if (thumbs.length < 2 || !stage) return;
    const ui = controls(); stage.after(ui.root);
    stage.tabIndex = 0; stage.setAttribute('role', 'group'); stage.setAttribute('aria-label', labels.gallery);
    const index = () => Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-pressed') === 'true'));
    const sync = () => { const text = `${index() + 1} / ${thumbs.length}`; if (ui.count.textContent !== text) ui.count.textContent = text; };
    const select = (target, pointer) => {
      const next = (target + thumbs.length) % thumbs.length;
      const previous = index();
      thumbs[next].dispatchEvent(new MouseEvent('click', { bubbles: true, detail: pointer ? 1 : 0 }));
      if (next !== previous) reveal(ui.count, pointer, next > previous ? 1 : -1);
      sync();
    };
    ui.prev.addEventListener('click', event => select(index() - 1, event.detail > 0), { signal });
    ui.next.addEventListener('click', event => select(index() + 1, event.detail > 0), { signal });
    gallery.addEventListener('click', sync, { signal });
    stage.addEventListener('keydown', event => {
      if (event.target !== stage || !['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
      event.preventDefault();
      select(event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1 : index() + (event.key === 'ArrowRight' ? 1 : -1), false);
    }, { signal });
    let touch;
    stage.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch' || !event.isPrimary || event.clientX < 24 || event.clientX > innerWidth - 24 || event.target.closest('button,a,input,video')) return;
      touch = { x: event.clientX, y: event.clientY, id: event.pointerId, time: event.timeStamp };
    }, { signal, passive: true });
    stage.addEventListener('pointerup', event => {
      if (!touch || event.pointerId !== touch.id) return;
      const dx = event.clientX - touch.x; const dy = event.clientY - touch.y;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.5 && event.timeStamp - touch.time < 800) select(index() + (dx < 0 ? 1 : -1), true);
      touch = null;
    }, { signal, passive: true });
    stage.addEventListener('pointercancel', () => { touch = null; }, { signal });
    const observer = new MutationObserver(sync);
    thumbs.forEach(thumb => observer.observe(thumb, { attributes: true, attributeFilter: ['aria-pressed'] }));
    sync(); cleanups.push(() => { observer.disconnect(); ui.root.remove(); stage.removeAttribute('tabindex'); stage.removeAttribute('role'); stage.removeAttribute('aria-label'); });
  }));
  const steps = root => root.querySelectorAll('.hw-steps').forEach(list => enhance(list, (signal, cleanups) => {
    const items = [...list.children];
    const buttons = [];
    list.classList.add('is-interactive');
    items.forEach((item, i) => {
      const title = item.querySelector('strong'); const copy = item.querySelector('span');
      if (!title || !copy) return;
      const trigger = button(''); const number = document.createElement('span'); number.className = 'hydros-step-number'; number.textContent = String(i + 1).padStart(2, '0'); number.setAttribute('aria-hidden', 'true');
      trigger.append(number, title); item.prepend(trigger);
      copy.id = `${list.closest('[data-world-panel]').id}-step-${i}`;
      trigger.setAttribute('aria-controls', copy.id); trigger.setAttribute('aria-expanded', String(i === 0));
      copy.hidden = i !== 0; item.classList.toggle('is-open', i === 0); buttons.push(trigger);
      trigger.addEventListener('click', event => {
        if (trigger.getAttribute('aria-expanded') === 'true') return;
        items.forEach((other, j) => {
          buttons[j]?.setAttribute('aria-expanded', String(j === i));
          other.classList.toggle('is-open', j === i);
          const text = other.querySelector(':scope > span'); if (text) text.hidden = j !== i;
        });
        reveal(copy, event.detail > 0);
      }, { signal });
      cleanups.push(() => { item.prepend(title); trigger.remove(); copy.hidden = false; item.classList.remove('is-open'); });
    });
    cleanups.push(() => list.classList.remove('is-interactive'));
  }));
  const rails = root => root.querySelectorAll('[data-customer-review-rail]').forEach(rail => enhance(rail, (signal, cleanups) => {
    const items = [...rail.children]; if (items.length < 2) return;
    const ui = controls(); rail.after(ui.root);
    const index = () => Math.min(items.length - 1, Math.max(0, Math.round(rail.scrollLeft / (items[0].getBoundingClientRect().width + 24))));
    const sync = () => {
      const text = `${index() + 1} / ${items.length}`;
      if (ui.count.textContent !== text) ui.count.textContent = text;
      ui.root.hidden = rail.scrollWidth <= rail.clientWidth + 8;
      ui.prev.disabled = rail.scrollLeft <= 4;
      ui.next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    };
    const move = (direction, pointer) => rail.scrollBy({ left: direction * (items[0].getBoundingClientRect().width + 24), behavior: pointer && !reduced.matches ? 'smooth' : 'instant' });
    ui.prev.addEventListener('click', event => move(-1, event.detail > 0), { signal });
    ui.next.addEventListener('click', event => move(1, event.detail > 0), { signal });
    let pending;
    rail.addEventListener('scroll', () => { if (!pending) pending = requestAnimationFrame(() => { pending = null; sync(); }); }, { signal, passive: true });
    const observer = 'ResizeObserver' in window ? new ResizeObserver(sync) : null; observer?.observe(rail);
    sync(); cleanups.push(() => { observer?.disconnect(); cancelAnimationFrame(pending); ui.root.remove(); });
  }));
  const init = (root = document) => { cards(root); galleries(root); steps(root); rails(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-world-card],.hydros-pdp__gallery,.hw-steps,[data-customer-review-rail]').forEach(element => {
      const state = states.get(element); state?.events.abort(); state?.cleanups.forEach(cleanup => cleanup()); states.delete(element);
    });
    activeMotions.forEach((motion, element) => { if (event.target.contains(element)) { motion.cancel(); activeMotions.delete(element); } });
  });
})();
